import { useMutation } from '@apollo/client';
import type { InsertUserGroupUsersMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertUserGroupUsersDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertUserGroupUsersTRPC } from './useInsertUserGroupUsersTRPC';

type InsertUserGroupUsersInput = {
  objects: { UserGroupId: string; UserId: string }[];
};

export const useInsertUserGroupUsers = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertUserGroupUsersDocument,
    {
      update: (cache) => {
        evictField(cache, 'user_group_user');
        evictField(cache, 'user_group');
      },
    }
  );

  const trpcMutation = useInsertUserGroupUsersTRPC();

  const insertUserGroupUsers = async (
    variables: InsertUserGroupUsersInput
  ): Promise<InsertUserGroupUsersMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertUserGroupUsers(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert user group users');
    }

    return result.data;
  };

  return {
    insertUserGroupUsers,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
